import React, { useState, useEffect, useRef } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './LessonPlan.css';
import TopicQuestionStatsModal from './TopicQuestionStatsModal';
import SetTopicTargetLevelModal from './SetTopicTargetLevelModal';
import TopicSearchModal from './TopicSearchModal';
import EvaluationQuestionEditorModal from './EvaluationQuestionEditorModal';
import ScaffoldManagementModal from './ScaffoldManagementModal';

/**
 * LessonPlan Component
 * React implementation that matches the behavior of the Angular lesson plan
 * Uses plain React + jQuery for DOM manipulation and existing JavaScript utilities
 */
const LessonPlan = ({ moduleSemesterId }) => {
  // State management
  const [lessonPlanWeeks, setLessonPlanWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [showQuestionStats, setShowQuestionStats] = useState(false);
  const [showTargetLevel, setShowTargetLevel] = useState(false);
  const [showTopicSearch, setShowTopicSearch] = useState(false);
  const [showEvaluationEditor, setShowEvaluationEditor] = useState(false);
  const [showScaffoldModal, setShowScaffoldModal] = useState(false);
  const [scaffoldData, setScaffoldData] = useState(null);
  const [isCoordinator, setIsCoordinator] = useState(true);
  const [weekForTopicSearch, setWeekForTopicSearch] = useState(null);

  // Modal data
  const [nodesInLessonPlan, setNodesInLessonPlan] = useState([]);
  const [topicToSetTargetLevel, setTopicToSetTargetLevel] = useState(null);
  const [topicQuestionStats, setTopicQuestionStats] = useState([]);
  const [evaluationEditorData, setEvaluationEditorData] = useState(null);

  // Refs for jQuery compatibility
  const lessonPlanTableRef = useRef(null);

  /**
   * Check if user is coordinator - uses existing UserUtilities
   */
  const checkIsCoordinator = (moduleSemesterId) => {
    try {
      if (window.UserUtilities) {
        const userUtils = new window.UserUtilities();
        return userUtils.isCoordinator(moduleSemesterId);
      }
      return true; // Default to true if utilities not available
    } catch (error) {
      console.warn('Error checking coordinator status:', error);
      return true;
    }
  };

  /**
   * Get session token - uses existing CheckSessionToken function
   */
  const getSessionToken = () => {
    try {
      if (window.CheckSessionToken) {
        return window.CheckSessionToken();
      }
      // Fallback to cookie parsing
      const cookieValue = document.cookie.split(';').find(row => row.trim().startsWith('token='))?.split('=')[1];
      return cookieValue?.trim() || '';
    } catch (error) {
      console.warn('Error getting session token:', error);
      return '';
    }
  };

  /**
   * Encode token - uses existing encodeToken function
   */
  const encodeToken = (token) => {
    try {
      if (window.encodeToken) {
        return window.encodeToken(token);
      }
      // Fallback to encodeObject if available
      if (window.encodeObject) {
        return window.encodeObject(token);
      }
      return encodeURIComponent(token);
    } catch (error) {
      console.warn('Error encoding token:', error);
      return encodeURIComponent(token);
    }
  };
  /**
   * API call to get lesson plan weeks - matches Angular implementation exactly
   * Based on angular/app.js GetLessonPlanWeeks function
   */
  const getLessonPlanWeeks = async (moduleSemesterId, token) => {
    try {
      const requestBody = {
        token: encodeToken(token || getTokenForRequest()),
        modulesemesterid: moduleSemesterId
      };
      
      const response = await fetch('./webservices/Organization.svc/GetLessonPlanWeeks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`Failed to get lesson plan weeks: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      
      // Handle .NET service response format (might be wrapped in 'd' property)
      return data.d || data;
    } catch (error) {
      console.error('Error getting lesson plan weeks:', error);
      throw error;
    }
  };

  /**
   * API call to update lesson plan weeks - equivalent to helper.updateLessonPlanWeeks()
   */
  const updateLessonPlanWeeks = async (moduleSemesterId, plantopics, token) => {
    try {
      const response = await fetch('./webservices/Organization.svc/UpdateLessonPlanWeeks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          token: encodeToken(token),
          modulesemesterid: moduleSemesterId,
          plantopics: plantopics
        }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to update lesson plan weeks');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating lesson plan weeks:', error);
      throw error;
    }
  };

  /**
   * API call to get topic question statistics
   * Calls webservices/Question.svc/alltopicsqstatistics (matching Angular implementation)
   */
  const getQuestionStatisticsForAllTopics = async (token, topicsInLessonPlan) => {
    try {
      const response = await fetch('./webservices/Question.svc/alltopicsqstatistics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodeToken(token),
          weeklyPlan: JSON.stringify(topicsInLessonPlan)
        }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to get question statistics');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting question statistics:', error);
      throw error;
    }
  };

  /**
   * API call to get topic Q status - matches Angular implementation
   * Based on angular/app.js GetTopicQStatus function
   */
  const getTopicQStatus = async (token, topicsInLessonPlan) => {
    try {
      const response = await fetch('./webservices/Question.svc/topicqstatistics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          token: encodeToken(token),
          weeklyPlan: topicsInLessonPlan
        }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Handle .NET service response format
      if (result && result.d) {
        return result.d;
      } else if (Array.isArray(result)) {
        return result;
      } else if (result && result.data) {
        return result.data;
      } else {
        return result || [];
      }
    } catch (error) {
      console.error('Error in direct API call for topic Q status:', error);
      throw error;
    }
  };

  /**
   * Check if topic has scaffolds
   */
  const checkTopicHasScaffolds = async (token, topicId) => {
    try {
      const response = await fetch(`./webservices/Scaffold.svc/GetModuleSemesterScaffolds?token=${encodeToken(token)}&topicId=${topicId}&msId=${moduleSemesterId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.warn(`Scaffold check failed for topic ${topicId}: ${response.status}`);
        return false;
      }

      const result = await response.json();
      // Handle .NET service response format (might be wrapped in 'd' property)
      const scaffolds = result.d ? (result.d.Scaffolds || result.d) : result.Scaffolds;
      const hasScaffolds = scaffolds && Array.isArray(scaffolds) && scaffolds.length > 0;
      return hasScaffolds;
    } catch (error) {
      console.error('Error checking scaffolds for topic:', topicId, error);
      return false;
    }
  };

  /**
   * Check if topic has evaluation and if it's complete
   */
  const checkTopicHasEvaluation = async (token, topicId) => {
    try {
      // First check if evaluation exists
      const hasEvalResponse = await fetch('./webservices/Evaluation.svc/HasEvaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodeToken(token),
          topicId: topicId,
          msId: moduleSemesterId
        }),
        credentials: 'include',
      });

      if (!hasEvalResponse.ok) {
        return { hasEvaluation: false, evaluationComplete: false };
      }

      const hasEvalResult = await hasEvalResponse.json();
      // Handle .NET service response format (might be wrapped in 'd' property)
      const hasEval = hasEvalResult.d !== undefined ? hasEvalResult.d : hasEvalResult;
      if (!hasEval) {
        return { hasEvaluation: false, evaluationComplete: false };
      }

      // If evaluation exists, check if it has questions
      const evalResponse = await fetch('./webservices/Evaluation.svc/GetEvaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodeToken(token),
          topicId: topicId,
          msId: moduleSemesterId
        }),
        credentials: 'include',
      });

      if (!evalResponse.ok) {
        return { hasEvaluation: true, evaluationComplete: false };
      }

      const evalDataResult = await evalResponse.json();
      // Handle .NET service response format (might be wrapped in 'd' property)
      const evalData = evalDataResult.d ? evalDataResult.d : evalDataResult;
      const questions = evalData.Questions || (Array.isArray(evalData) ? evalData : []);
      const hasQuestions = questions && questions.length > 0;
      
      
      return {
        hasEvaluation: true,
        evaluationComplete: hasQuestions
      };
    } catch (error) {
      console.error('Error checking evaluation for topic:', topicId, error);
      return { hasEvaluation: false, evaluationComplete: false };
    }
  };

  /**
   * Load topic statuses (scaffolds and evaluation) for all topics
   */
  const loadTopicStatuses = async (weeks) => {
    try {
      const token = getTokenForRequest();

      const updatedWeeks = await Promise.all(
        weeks.map(async (week) => {
          if (!week.Topics || week.Topics.length === 0) {
            return week;
          }

          const updatedTopics = await Promise.all(
            week.Topics.map(async (topic) => {
              const topicId = topic.TopicID || topic.topic_id || topic.ID;
              if (!topicId) {
                return topic;
              }

              // Check scaffolds and evaluation in parallel
              const [hasScaffolds, evalStatus] = await Promise.all([
                checkTopicHasScaffolds(token, topicId),
                checkTopicHasEvaluation(token, topicId)
              ]);

              // Ensure boolean values
              const hasScaffoldsBool = hasScaffolds === true || hasScaffolds === 'true';
              const hasEvaluationBool = evalStatus.hasEvaluation === true || evalStatus.hasEvaluation === 'true';
              const evaluationCompleteBool = evalStatus.evaluationComplete === true || evalStatus.evaluationComplete === 'true';


              return {
                ...topic,
                HasScaffolds: hasScaffoldsBool,
                HasEvaluation: hasEvaluationBool,
                EvaluationComplete: evaluationCompleteBool
              };
            })
          );

          return {
            ...week,
            Topics: updatedTopics
          };
        })
      );

      return updatedWeeks;
    } catch (error) {
      console.error('Error loading topic statuses:', error);
      return weeks; // Return original weeks if error
    }
  };

  /**
   * Load lesson plan data on component mount
   */
  useEffect(() => {
    const loadLessonPlan = async () => {
      try {
        setLoading(true);
        setError(null);
        setMessage('');
        
        const token = getTokenForRequest();
        const coordinatorStatus = checkIsCoordinator(moduleSemesterId);
        setIsCoordinator(coordinatorStatus);
        
        const result = await getLessonPlanWeeks(moduleSemesterId, token);
        
        // Handle both array and object responses
        let lessonWeeks = result;
        
        // If result is already an array, use it directly
        if (Array.isArray(result)) {
          lessonWeeks = result;
        } else if (result && typeof result === 'object') {
          // If result is wrapped in an object, try to extract the array
          if (result.d !== undefined) {
            lessonWeeks = result.d;
          } else if (result.weeks) {
            lessonWeeks = result.weeks;
          } else if (result.data) {
            lessonWeeks = result.data;
          } else if (result.GetLessonPlanWeeksResult) {
            // Some .NET services wrap in method name
            lessonWeeks = result.GetLessonPlanWeeksResult;
          } else {
            // Try to find any array property
            const arrayKeys = Object.keys(result).filter(key => Array.isArray(result[key]));
            if (arrayKeys.length > 0) {
              lessonWeeks = result[arrayKeys[0]];
            }
          }
        }
        
        // Ensure we have an array
        if (!Array.isArray(lessonWeeks)) {
          console.warn('Result is not an array. Result:', lessonWeeks);
          lessonWeeks = [];
        }
        
        // Load topic statuses (scaffolds and evaluation) before setting state
        const weeksWithStatuses = await loadTopicStatuses(lessonWeeks);
        setLessonPlanWeeks(weeksWithStatuses);
          
          // Load topic question statistics
          if (weeksWithStatuses.length > 0) {
            await loadTopicQuestionStats();
          }
          
        } catch (err) {
          console.error('Error loading lesson plan:', err);
          setError(err.message || 'Failed to load lesson plan data');
        } finally {
          setLoading(false);
        }
      };
  
      if (moduleSemesterId) {
        loadLessonPlan();
      }
    }, [moduleSemesterId]);
  
    /**
     * Save lesson plan - equivalent to the save button click in Angular
     */
    const handleSaveLessonPlan = async () => {
      try {
        if (!confirm("Are you sure you want to save the current lesson plan? All previous lesson plan will be deleted.")) {
          return;
        }
  
        const token = getTokenForRequest();
        const plantopics = collectTopicsFromTable();
        
        await updateLessonPlanWeeks(moduleSemesterId, plantopics, token);
        
        setMessage('Lesson plan saved successfully!');
        
        // Reload lesson plan data
        const result = await getLessonPlanWeeks(moduleSemesterId, token);
        
        // Handle both array and object responses
        let lessonWeeks = result;
        
        // If result is already an array, use it directly
        if (Array.isArray(result)) {
          lessonWeeks = result;
        } else if (result && typeof result === 'object') {
          // If result is wrapped in an object, try to extract the array
          if (result.d !== undefined) {
            lessonWeeks = result.d;
          } else if (result.weeks) {
            lessonWeeks = result.weeks;
          } else if (result.data) {
            lessonWeeks = result.data;
          } else if (result.GetLessonPlanWeeksResult) {
            // Some .NET services wrap in method name
            lessonWeeks = result.GetLessonPlanWeeksResult;
          } else {
            // Try to find any array property
            const arrayKeys = Object.keys(result).filter(key => Array.isArray(result[key]));
            if (arrayKeys.length > 0) {
              lessonWeeks = result[arrayKeys[0]];
            }
          }
        }
        
        // Ensure we have an array
        if (!Array.isArray(lessonWeeks)) {
          console.warn('Result is not an array. Result:', lessonWeeks);
          lessonWeeks = [];
        }
        
        // Load topic statuses (scaffolds and evaluation) before setting state
        const weeksWithStatuses = await loadTopicStatuses(lessonWeeks);
        setLessonPlanWeeks(weeksWithStatuses);
        
        // Load and show topic question statistics after saving
        await loadTopicQuestionStats();
        setShowQuestionStats(true);
        
      } catch (error) {
        console.error('Error saving lesson plan:', error);
        alert('Unable to update lesson plan');
      }
    };

    /**
     * Remove topic from week
     */
    const handleRemoveTopic = (weekIndex, topicIndex, event) => {
      event.stopPropagation(); // Prevent week row click and topic click
      
      const topic = lessonPlanWeeks[weekIndex].Topics[topicIndex];
      if (confirm(`Are you sure you want to remove "${topic.TopicName}" from Week ${lessonPlanWeeks[weekIndex].WeekNo}?`)) {
        const updatedWeeks = [...lessonPlanWeeks];
        updatedWeeks[weekIndex].Topics.splice(topicIndex, 1);
        setLessonPlanWeeks(updatedWeeks);
        
        setMessage(`Topic "${topic.TopicName}" removed from Week ${lessonPlanWeeks[weekIndex].WeekNo}`);
      }
    };
  
    /**
     * Collect topics from table - equivalent to jQuery DOM parsing in original
     * Builds the plantopics array for the UpdateLessonPlanWeeks API call
     */
    const collectTopicsFromTable = () => {
      const topics = [];
      
      
      lessonPlanWeeks.forEach((week, weekIndex) => {
        if (week.Topics && Array.isArray(week.Topics)) {
          week.Topics.forEach((topic, topicIndex) => {
            // Format the start date as .NET format like the example
            let startDate = week.StartDate;
            
            // Convert to .NET date format if not already
            if (typeof startDate === 'number') {
              // If it's a timestamp, convert to .NET format
              startDate = `/Date(${startDate}+0800)/`;
            } else if (typeof startDate === 'string' && !startDate.includes('/Date(')) {
              // If it's a date string, convert to timestamp then .NET format
              const timestamp = new Date(startDate).getTime();
              startDate = `/Date(${timestamp}+0800)/`;
            }
            
            const topicData = {
              TopicID: (topic.TopicID || topic.topic_id || topic.ID).toString(),
              StartDate: startDate
            };
            
            // Add TargetLevel if it exists
            if (topic.TargetLevel) {
              topicData.TargetLevel = topic.TargetLevel.toString();
            }
            
            topics.push(topicData);
          });
        }
      });
      
      return topics;
    };
  
    /**
     * Load topic question statistics
     */
    const loadTopicQuestionStats = async (currentTopicId = null) => {
      try {
        const token = getTokenForRequest();
        
        // Collect topics in lesson plan and format according to API requirements
        const topicsInLessonPlan = [];
        let sequenceNo = 1;
        
        lessonPlanWeeks.forEach((week, weekIndex) => {
          if (week.Topics && Array.isArray(week.Topics)) {
            week.Topics.forEach((topic, topicIndex) => {
              // Convert StartDate to timestamp if it's not already
              let startDateTimestamp = week.StartDate;
              if (typeof startDateTimestamp === 'string' && startDateTimestamp.includes('/Date(')) {
                // Handle .NET date format /Date(1234567890)/
                startDateTimestamp = parseInt(startDateTimestamp.match(/\d+/)[0]);
              } else if (typeof startDateTimestamp === 'string') {
                // Handle regular date string
                startDateTimestamp = new Date(startDateTimestamp).getTime();
              } else if (typeof startDateTimestamp === 'number') {
                // Already a timestamp
                startDateTimestamp = startDateTimestamp;
              }
              
              // Ensure we have the required data
              const topicId = topic.TopicID || topic.topic_id || topic.ID;
              const topicName = topic.TopicName || topic.topic_name || topic.name || `Topic ${topicId}`;
              
              // Check if this is the currently viewed topic
              const isCurrentTopic = currentTopicId && topicId.toString() === currentTopicId.toString();
              
              if (topicId) {
                topicsInLessonPlan.push({
                  sequence_no: sequenceNo++,
                  topic_id: topicId.toString(),
                  topic_name: topicName,
                  start_date: Math.floor(startDateTimestamp / 1000), // Convert to seconds
                  is_currently_modified: currentTopicId ? isCurrentTopic : true // Default to true when getting all topics
                });
              }
            });
          }
        });
  
        if (topicsInLessonPlan.length === 0) {
          return;
        }
  
        // Use the GetQuestionStatisticsForAllTopics service as per Angular implementation
        const stats = await getQuestionStatisticsForAllTopics(token, topicsInLessonPlan);
        
        // Process stats for modal display
        const processedStats = [];
        
        // Flatten the nested array structure from the API response
        const flattenedStats = stats.flat();
        
        topicsInLessonPlan.forEach(topic => {
          const topicStats = flattenedStats.filter(stat => stat.TopicID === parseInt(topic.topic_id));
          
          const nodeStat = {
            name: topic.topic_name,
            level1: 0,
            level2: 0,
            level3: 0,
            level4: 0
          };
          
          topicStats.forEach(stat => {
            switch(stat.Difficulty) {
              case 1: nodeStat.level1 = stat.NumberOfFinalized; break;
              case 2: nodeStat.level2 = stat.NumberOfFinalized; break;
              case 3: nodeStat.level3 = stat.NumberOfFinalized; break;
              case 4: nodeStat.level4 = stat.NumberOfFinalized; break;
            }
          });
          
          processedStats.push(nodeStat);
        });
        
        setNodesInLessonPlan(processedStats);
        
      } catch (error) {
        console.error('Error loading topic question stats:', error);
      }
    };
  
  /**
   * Handle topic click to update target level
   * React implementation that matches the behavior of the original Angular function
   */
  const handleUpdateTopicTarget = async (topic, week) => {
    try {
      
      const topicId = topic?.TopicID ?? topic?.topic_id ?? topic?.ID;
      const topicIdStr = topicId != null ? String(topicId) : '';

      // MSGoals from getLessonPlanWeeks comes as:
      // [{ Key: <topicID>, Value: { TargetLevel: <1..4>, ... } }, ...]
      let targetLevelFromMsGoals = null;
      if (week?.MSGoals) {
        if (Array.isArray(week.MSGoals)) {
          const entry = week.MSGoals.find(g => String(g?.Key) === topicIdStr);
          targetLevelFromMsGoals = entry?.Value?.TargetLevel;
        } else if (typeof week.MSGoals === 'object') {
          const entry =
            week.MSGoals[topicIdStr] ??
            week.MSGoals[topicId] ??
            (typeof week.MSGoals.find === 'function' ? week.MSGoals.find(g => String(g?.Key) === topicIdStr) : null);
          targetLevelFromMsGoals = entry?.Value?.TargetLevel ?? entry?.TargetLevel ?? entry?.targetLevel ?? null;
        }
      }

      // Set up topic data for modal
      const topicData = {
        id: topicId,
        value: topic.TopicName,
        TopicName: topic.TopicName,
        startdate: week?.StartDate,
        targetLevel:
          targetLevelFromMsGoals != null && targetLevelFromMsGoals !== ''
            ? String(targetLevelFromMsGoals)
            : (topic?.TargetLevel != null && topic.TargetLevel !== '' ? String(topic.TargetLevel) : '4'),
        needEvaluation: topic.HasEvaluation || false,
        isExistingTopic: true
      };
      
      setTopicToSetTargetLevel(topicData);
      
      // Load question statistics for this specific topic
      await loadTopicQuestionStatistics(topicId, week?.StartDate, topic.TopicName);
      
      // Show the modal
      setShowTargetLevel(true);
      
    } catch (error) {
      console.error('Error in handleUpdateTopicTarget:', error);
      alert('Error loading topic information: ' + error.message);
    }
  };
  
    /**
     * Load question statistics for a specific topic
     * Uses the same API endpoint as the original implementation
     */
    const loadTopicQuestionStatistics = async (topicId, startdate, topicName = null) => {
      try {
        const token = getTokenForRequest();
        
        // Build complete weekly plan with all topics, marking the current one as modified
        const topicsInLessonPlan = [];
        let sequenceNo = 1;
        let currentTopicFound = false;
        
        lessonPlanWeeks.forEach((week, weekIndex) => {
          if (week.Topics && Array.isArray(week.Topics)) {
            week.Topics.forEach((topic, topicIndex) => {
              // Convert StartDate to timestamp
              let startDateTimestamp = week.StartDate;
              if (typeof startDateTimestamp === 'string' && startDateTimestamp.includes('/Date(')) {
                startDateTimestamp = parseInt(startDateTimestamp.match(/\d+/)[0]);
              } else if (typeof startDateTimestamp === 'string') {
                startDateTimestamp = new Date(startDateTimestamp).getTime();
              } else if (typeof startDateTimestamp === 'number') {
                startDateTimestamp = startDateTimestamp;
              }
              
              const thisTopicId = topic.TopicID || topic.topic_id || topic.ID;
              const thisTopicName = topic.TopicName || topic.topic_name || topic.name || `Topic ${thisTopicId}`;
              
              // Check if this is the currently viewed topic
              const isCurrentTopic = thisTopicId.toString() === topicId.toString();
              if (isCurrentTopic) {
                currentTopicFound = true;
              }
              
              if (thisTopicId) {
                topicsInLessonPlan.push({
                  sequence_no: sequenceNo++,
                  topic_id: thisTopicId.toString(),
                  topic_name: thisTopicName,
                  start_date: Math.floor(startDateTimestamp / 1000),
                  is_currently_modified: isCurrentTopic
                });
              }
            });
          }
        });
        
        // If the topic wasn't found in existing lesson plan, add it as a single topic
        if (!currentTopicFound) {
          let startDateTimestamp = startdate;
          if (typeof startDateTimestamp === 'string' && startDateTimestamp.includes('/Date(')) {
            startDateTimestamp = parseInt(startDateTimestamp.match(/\d+/)[0]);
          } else if (typeof startDateTimestamp === 'string') {
            startDateTimestamp = new Date(startDateTimestamp).getTime();
          }
          
          topicsInLessonPlan.push({
            sequence_no: sequenceNo,
            topic_id: topicId.toString(),
            topic_name: topicName || `Topic ${topicId}`,
            start_date: Math.floor(startDateTimestamp / 1000),
            is_currently_modified: true
          });
        }
        
        
        // Direct API call to get topic question statistics
        const response = await fetch('./webservices/Question.svc/topicqstatistics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8'
          },
          body: JSON.stringify({
            token: encodeToken(token),
            weeklyPlan: JSON.stringify(topicsInLessonPlan)
          }),
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load question statistics: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Extract statistics from response
        let statistics = [];
        if (data && data.d) {
          statistics = Array.isArray(data.d) ? data.d : [data.d];
        } else if (Array.isArray(data)) {
          statistics = data;
        } else if (data) {
          statistics = [data];
        }
        
        
        // Ensure we have statistics for all difficulty levels (1-4)
        const completeStats = [];
        for (let difficulty = 1; difficulty <= 4; difficulty++) {
          const existingStat = statistics.find(stat => stat.Difficulty === difficulty);
          completeStats.push({
            TopicID: topicId,
            Difficulty: difficulty,
            NumberOfFinalized: existingStat ? existingStat.NumberOfFinalized : 0
          });
        }
        
        setTopicQuestionStats(completeStats);
        
        return completeStats;
        
      } catch (error) {
        console.error('Error loading topic question statistics:', error);
        
        // Create default statistics if API fails
        const defaultStats = [];
        for (let difficulty = 1; difficulty <= 4; difficulty++) {
          defaultStats.push({
            TopicID: topicId,
            Difficulty: difficulty,
            NumberOfFinalized: 0
          });
        }
        
        setTopicQuestionStats(defaultStats);
        
        throw error;
      }
    };
  
  /**
   * Handle clicking on week row to add new topics
   * Matches Angular implementation: click on week row to add topics
   */
  const handleWeekRowClick = (week, event) => {
    // Only handle clicks on the topics column that don't have existing topics
    if (event.target.closest('.react-token-input-list-item')) {
      return; // Don't handle if clicking on existing topic
    }
    
    
    // Show topic search modal or interface (matches Angular behavior)
    showTopicSearchForWeek(week);
  };
  
    /**
     * Show topic search interface for a specific week
     * Now uses a proper modal instead of prompt
     */
    const showTopicSearchForWeek = async (week) => {
      try {
        
        // Set the week for the search modal
        setWeekForTopicSearch(week);
        
        // Show the topic search modal
        setShowTopicSearch(true);
        
      } catch (error) {
        console.error('Error showing topic search:', error);
        alert('Error opening topic search');
      }
    };
  
    /**
     * Handle topic selection from the search modal
     */
    const handleTopicSelectedFromSearch = async (selectedTopic, week) => {
      try {
        
        // Check if topic already exists in lesson plan (any week)
        // Convert both to strings for comparison to handle type mismatches
        const selectedTopicId = String(selectedTopic.ID || selectedTopic.id || selectedTopic.Id);
        const existingTopic = lessonPlanWeeks.some(w => 
          w.Topics && w.Topics.some(t => {
            const existingTopicId = String(t.TopicID || t.topic_id || t.ID || t.id);
            return existingTopicId === selectedTopicId;
          })
        );
        
        if (existingTopic) {
          alert('You cannot add the same topic twice. This topic is already in the lesson plan.');
          // Don't close modal - let user select a different topic
          return;
        }
        
        // Check if topic already exists in the specific week
        const existingTopics = week.Topics || [];
        const isDuplicateInWeek = existingTopics.some(existingTopic => {
          const existingTopicId = String(existingTopic.TopicID || existingTopic.topic_id || existingTopic.ID || existingTopic.id);
          const existingTopicName = (existingTopic.TopicName || existingTopic.topic_name || existingTopic.name || '').toLowerCase();
          const selectedTopicName = (selectedTopic.name || selectedTopic.Name || selectedTopic.Desc || '').toLowerCase();
          
          return existingTopicId === selectedTopicId || 
                 (existingTopicName && selectedTopicName && existingTopicName === selectedTopicName);
        });
        
        if (isDuplicateInWeek) {
          alert(`Topic "${selectedTopic.name || selectedTopic.Name || 'Unknown'}" is already added to Week ${week.WeekNo}. Please select a different topic.`);
          // Don't close modal - let user select a different topic
          return;
        }
        
        // Close topic search modal only if no duplicate
        setShowTopicSearch(false);
        
        // Show the target level modal for the new topic
        await handleAddNewTopic(selectedTopic, week);
        
      } catch (error) {
        console.error('Error handling topic selection:', error);
        alert('Error adding selected topic: ' + error.message);
      }
    };
  
    /**
     * Handle adding a new topic to a week
     */
    const handleAddNewTopic = async (topic, week) => {
      try {
        
        // Check for duplicates first (defensive check - should already be caught earlier)
        const existingTopics = week.Topics || [];
        const topicId = String(topic.ID || topic.id || topic.Id);
        const topicName = (topic.name || topic.Name || topic.Desc || '').toLowerCase();
        
        const isDuplicate = existingTopics.some(existingTopic => {
          const existingTopicId = String(existingTopic.TopicID || existingTopic.topic_id || existingTopic.ID || existingTopic.id);
          const existingTopicName = (existingTopic.TopicName || existingTopic.topic_name || existingTopic.name || '').toLowerCase();
          
          return existingTopicId === topicId || 
                 (existingTopicName && topicName && existingTopicName === topicName);
        });
        
        if (isDuplicate) {
          alert(`Topic "${topic.name || topic.Name || 'Unknown'}" is already added to Week ${week.WeekNo}. Please select a different topic.`);
          return;
        }
        
        
        // Set up topic for modal
        const topicData = {
          id: topic.ID,
          value: topic.name,
          TopicName: topic.name,
          startdate: week.StartDate,
          targetLevel: '4', // Default to Expert
          needEvaluation: false,
          isNewTopic: true // Flag to indicate this is a new topic being added
        };
        
        setTopicToSetTargetLevel(topicData);
        
        // Load question statistics for this specific topic using the new function
        await loadTopicQuestionStatistics(topic.ID, week.StartDate, topic.name);
        
        // Show modal
        setShowTargetLevel(true);
        
      } catch (error) {
        console.error('Error adding new topic:', error);
        // Even if stats loading fails, still show the modal with default stats
        setShowTargetLevel(true);
      }
    };
  
  /**
   * Handle saving topic target level
   * React implementation that matches the behavior of the original function
   */
  const handleSaveTopicTargetLevel = async (updatedTopic) => {
    try {
      
      const token = getTokenForRequest();
      
      // If this is a new topic being added, update the lesson plan data
      if (updatedTopic.isNewTopic) {
        
        // Find the week to add the topic to
        const updatedWeeks = lessonPlanWeeks.map(week => {
          if (week.StartDate === updatedTopic.startdate) {
            const newTopic = {
              TopicID: updatedTopic.id,
              TopicName: updatedTopic.TopicName,
              TargetLevel: updatedTopic.targetLevel,
              NeedEvaluation: updatedTopic.needEvaluation,
              HasScaffolds: false,
              HasEvaluation: updatedTopic.needEvaluation,
              EvaluationComplete: false
            };
            
            return {
              ...week,
              Topics: week.Topics ? [...week.Topics, newTopic] : [newTopic]
            };
          }
          return week;
        });
        
        setLessonPlanWeeks(updatedWeeks);
      }
      
      // Check if user is coordinator and set MS goal
      if (isCoordinator) {
        try {
          const response = await fetch('./webservices/Organization.svc/msgoal', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token: encodeToken(token),
              moduleSemesterID: moduleSemesterId,
              topicID: updatedTopic.id,
              targetLevel: updatedTopic.targetLevel
            }),
            credentials: 'include',
          });
          
          if (!response.ok) {
            console.warn('SetMSGoal API call failed, but continuing...');
          } else {
            // Keep the UI dropdown in sync immediately.
            // getLessonPlanWeeks returns week.MSGoals as:
            // [{ Key: <topicID>, Value: { TargetLevel: <1..4>, ... } }, ...]
            const topicIdStr = updatedTopic.id != null ? String(updatedTopic.id) : '';
            const targetLevelInt = parseInt(updatedTopic.targetLevel, 10);
            const targetLevelValue = Number.isNaN(targetLevelInt) ? updatedTopic.targetLevel : targetLevelInt;

            setLessonPlanWeeks(prevWeeks =>
              prevWeeks.map(week => {
                if (!week) return week;

                // Target week match: StartDate from the modal is week.StartDate
                if (updatedTopic.startdate != null && String(week.StartDate) !== String(updatedTopic.startdate)) {
                  return week;
                }

                // Update MSGoals
                let nextMSGoals = week.MSGoals;
                if (Array.isArray(week.MSGoals)) {
                  nextMSGoals = week.MSGoals.map(g => {
                    const keyStr = g?.Key != null ? String(g.Key) : '';
                    if (keyStr !== topicIdStr) return g;
                    return {
                      ...g,
                      Value: {
                        ...(g.Value || {}),
                        TargetLevel: targetLevelValue
                      }
                    };
                  });
                } else if (week.MSGoals && typeof week.MSGoals === 'object') {
                  // Fallback for non-array shapes
                  const entry = week.MSGoals[topicIdStr] || week.MSGoals[updatedTopic.id];
                  if (entry) {
                    nextMSGoals = {
                      ...week.MSGoals,
                      [topicIdStr]: {
                        ...(entry || {}),
                        TargetLevel: targetLevelValue
                      }
                    };
                  }
                }

                // Update topic TargetLevel in the week (helps other UI paths)
                const nextTopics = Array.isArray(week.Topics)
                  ? week.Topics.map(t => {
                      const tIdStr = t?.TopicID != null ? String(t.TopicID) : (t?.topic_id != null ? String(t.topic_id) : (t?.ID != null ? String(t.ID) : ''));
                      if (tIdStr !== topicIdStr) return t;
                      return { ...t, TargetLevel: targetLevelValue };
                    })
                  : week.Topics;

                return {
                  ...week,
                  MSGoals: nextMSGoals,
                  Topics: nextTopics
                };
              })
            );
          }
        } catch (error) {
          console.warn('Error setting MS goal:', error);
          // Continue anyway - this might not be critical
        }
      }
      
      // If topic needs evaluation, create evaluation
      if (updatedTopic.needEvaluation) {
        try {
          const evaluationResponse = await fetch('./webservices/Evaluation.svc/CreateEvaluation', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token: encodeToken(token),
              topicIds: [updatedTopic.id],
              msId: moduleSemesterId
            }),
            credentials: 'include',
          });
          
          if (!evaluationResponse.ok) {
            throw new Error(`Failed to create evaluation for topic ${updatedTopic.value}`);
          }
          
          setMessage(`Topic "${updatedTopic.value}" added successfully with evaluation!`);
          
          // Update topic status to reflect evaluation creation
          await refreshTopicStatus(updatedTopic.id);
          
          // Close the target level modal
          setShowTargetLevel(false);
          
          // Open evaluation question editor modal
          setEvaluationEditorData({
            topicId: updatedTopic.id,
            moduleSemesterId: moduleSemesterId,
            topicName: updatedTopic.value
          });
          
          // Load topic question statistics with the current topic ID
          await loadTopicQuestionStats(updatedTopic.id);
          setShowEvaluationEditor(true);
          
        } catch (error) {
          console.error('Error creating evaluation:', error);
          setMessage(`Topic "${updatedTopic.value}" added successfully, but evaluation creation failed.`);
        }
      } else {
        setMessage(`Topic "${updatedTopic.value}" added successfully!`);
        
        // Close the target level modal
        setShowTargetLevel(false);
      }
      
    } catch (error) {
      console.error('Error saving topic target level:', error);
      alert('Failed to save topic target level');
    }
  };

  /**
   * Refresh status for a single topic (scaffolds and evaluation)
   */
  const refreshTopicStatus = async (topicId) => {
    try {
      if (!topicId) return;
      const token = getTokenForRequest();

      const [hasScaffolds, evalStatus] = await Promise.all([
        checkTopicHasScaffolds(token, topicId),
        checkTopicHasEvaluation(token, topicId)
      ]);

      // Ensure boolean values
      const hasScaffoldsBool = hasScaffolds === true || hasScaffolds === 'true';
      const hasEvaluationBool = evalStatus.hasEvaluation === true || evalStatus.hasEvaluation === 'true';
      const evaluationCompleteBool = evalStatus.evaluationComplete === true || evalStatus.evaluationComplete === 'true';


      // Update the topic in lessonPlanWeeks
      setLessonPlanWeeks(prevWeeks => 
        prevWeeks.map(week => ({
          ...week,
          Topics: week.Topics ? week.Topics.map(topic => {
            const thisTopicId = topic.TopicID || topic.topic_id || topic.ID;
            if (thisTopicId && thisTopicId.toString() === topicId.toString()) {
              return {
                ...topic,
                HasScaffolds: hasScaffoldsBool,
                HasEvaluation: hasEvaluationBool,
                EvaluationComplete: evaluationCompleteBool
              };
            }
            return topic;
          }) : []
        }))
      );
    } catch (error) {
      console.error('Error refreshing topic status:', error);
    }
  };

  /**
   * Handle topic click for scaffold editing
   * Matches Angular implementation: initTopicScaffoldEditting
   */
  const handleTopicScaffoldEditing = async (topic) => {
    try {
      const token = getTokenForRequest();
      const topicId = topic.TopicID;
      const topicName = topic.TopicName;
      const msID = moduleSemesterId;


      // Step 1: Get existing scaffolds for this topic
      const existingScaffoldsResponse = await fetch(`./webservices/Scaffold.svc/GetModuleSemesterScaffolds?token=${window.encodeToken(token)}&topicId=${topicId}&msId=${msID}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!existingScaffoldsResponse.ok) {
        throw new Error(`HTTP error! status: ${existingScaffoldsResponse.status}`);
      }

      const existingScaffoldsResult = await existingScaffoldsResponse.json();

      // Step 2: Get all available scaffolds for user to add
      const allScaffoldsResponse = await fetch(`./webservices/Scaffold.svc/getScaffolds?token=${encodeToken(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!allScaffoldsResponse.ok) {
        throw new Error(`HTTP error! status: ${allScaffoldsResponse.status}`);
      }

      const allScaffoldsResult = await allScaffoldsResponse.json();

      // Process all scaffolds like Angular version
      const processedAllScaffolds = allScaffoldsResult.map(scaffold => ({
        ...scaffold,
        FileType: scaffold.TypeDesc
      }));

      // Set up scaffold data for modal
      const topicScaffolds = {
        topicID: topicId,
        topicName: topicName,
        msID: msID,
        MsScaffolds: existingScaffoldsResult.Scaffolds || [],
        AllScaffolds: processedAllScaffolds
      };

      setScaffoldData(topicScaffolds);
      setShowScaffoldModal(true);

    } catch (error) {
      console.error('Error loading scaffold data:', error);
      alert('Failed to load scaffold data');
    }
  };

  /**
   * Handle scaffold modal close - refresh topic status
   */
  const handleScaffoldModalClose = async () => {
    if (scaffoldData && scaffoldData.topicID) {
      await refreshTopicStatus(scaffoldData.topicID);
    }
    setShowScaffoldModal(false);
  };

  /**
   * Handle evaluation editor close - refresh topic status
   */
  const handleEvaluationEditorClose = async () => {
    if (evaluationEditorData && evaluationEditorData.topicId) {
      await refreshTopicStatus(evaluationEditorData.topicId);
    }
    setShowEvaluationEditor(false);
  };
  
    /**
     * Format date like the original ConvertToDDMMYYYYY function
     */
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      try {
        // Handle .NET date format /Date(1234567890)/
        if (dateStr.includes('/Date(')) {
          const timestamp = parseInt(dateStr.match(/\d+/)[0]);
          const date = new Date(timestamp);
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        }
        
        // Handle regular date strings
        const date = new Date(dateStr);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        return dateStr; // Return original if parsing fails
      }
    };
  
    /**
     * Render lesson plan table
     */
    const renderLessonPlanTable = () => {
      
      if (loading) {
        return (
          <div className="text-center" style={{ padding: '40px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading lesson plan...</p>
          </div>
        );
      }
  
      if (error) {
        return (
          <div className="alert alert-danger">
            <h5>Error Loading Lesson Plan</h5>
            <p>{error}</p>
            <p className="small text-muted">
              Debug info: Module Semester ID = {moduleSemesterId}
            </p>
          </div>
        );
      }
  
      if (!lessonPlanWeeks || lessonPlanWeeks.length === 0) {
        return (
          <div className="no-lesson-plan">
            <div className="text-center" style={{ padding: '40px' }}>
              <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
              <h5>No Lesson Plan</h5>
              <p className="text-muted">No lesson plan weeks found for this module.</p>
              <p className="small text-muted">
                Module Semester ID: {moduleSemesterId}
              </p>
            </div>
          </div>
        );
      }
  
      return (
        <table 
          ref={lessonPlanTableRef}
          id="react-lessonplantable" 
          className="table table-striped"
        >
          <thead>
            <tr>
              <th>Week#</th>
              <th>Start Date</th>
              <th>Topics</th>
            </tr>
          </thead>
          <tbody>
            {lessonPlanWeeks.map((week, index) => {
              return (
              <tr key={week.WeekNo || index}>
                <td>{week.WeekNo || (index + 1)}</td>
                <td>{formatDate(week.StartDate)}</td>
                <td 
                  className="react-lessontopicscol" 
                  data-startdate={week.StartDate}
                  onClick={(event) => handleWeekRowClick(week, event)}
                  style={{ cursor: 'pointer' }}
                >
                  <ul className="react-token-input-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {week.Topics && week.Topics.length > 0 ? (
                      week.Topics.map((topic, topicIndex) => (
                        <li 
                          key={topicIndex}
                          className="react-token-input-list-item"
                          data-id={topic.TopicID}
                          data-startdate={week.StartDate}
                          data-topic-name={topic.TopicName}
                          onClick={(event) => {
                            event.stopPropagation(); // Prevent week row click
                            handleTopicScaffoldEditing(topic);
                          }}
                          onDoubleClick={(event) => {
                            event.stopPropagation(); // Prevent week row click
                            handleUpdateTopicTarget(topic, week);
                          }}
                          style={{ 
                            cursor: 'pointer', 
                            margin: '2px', 
                            padding: '4px 8px', 
                            backgroundColor: '#f0f0f0', 
                            borderRadius: '4px', 
                            display: 'inline-flex',
                            alignItems: 'center',
                            border: '1px solid #ccc',
                            position: 'relative',
                            maxWidth: '100%'
                          }}
                        >
                          <span className="react-token-p">
                            {topic.TopicName}
                            {/* Debug: Show status values */}
                            {process.env.NODE_ENV === 'development' && (
                              <span style={{ fontSize: '10px', color: '#999', marginLeft: '5px' }}>
                                [S:{String(topic.HasScaffolds)} E:{String(topic.HasEvaluation)}]
                              </span>
                            )}
                            {!!topic.HasScaffolds && (
                              <span 
                                style={{ 
                                  color: '#337ab7',
                                  marginLeft: '5px',
                                  marginRight: '5px',
                                  fontSize: '14px',
                                  display: 'inline-block',
                                  fontWeight: 'bold',
                                  lineHeight: '1',
                                  flexShrink: 0
                                }}
                                title="Has scaffolds"
                              >
                                🎥
                              </span>
                            )}
                            {!!topic.HasEvaluation && (
                              <span 
                                style={{ 
                                  color: !!topic.EvaluationComplete ? 'green' : 'orange',
                                  marginLeft: '5px',
                                  marginRight: '5px',
                                  fontSize: '14px',
                                  display: 'inline-block',
                                  fontWeight: 'bold',
                                  lineHeight: '1',
                                  flexShrink: 0
                                }}
                                title={!!topic.EvaluationComplete ? "Evaluation complete" : "Evaluation template set up"}
                              >
                                ✓
                              </span>
                            )}
                          </span>

                          {/* Edit/Difficulty Level button - positioned before remove button */}
                          <span 
                            className="react-token-edit"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleUpdateTopicTarget(topic, week);
                            }}
                            title="Edit target level / difficulty"
                            style={{
                              marginLeft: '5px',
                              marginRight: '5px',
                              fontSize: '12px',
                              display: 'inline-block',
                              cursor: 'pointer',
                              color: '#666',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              transition: 'all 0.2s ease',
                              flexShrink: 0
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#e0e0e0';
                              e.target.style.color = '#333';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#666';
                            }}
                          >
                            ⚙️
                          </span>

                          {/* Remove button (X) - positioned on the right */}
                          <span 
                            className="react-token-delete"
                            onClick={(event) => handleRemoveTopic(index, topicIndex, event)}
                            title="Remove topic from this week"
                            style={{ flexShrink: 0 }}
                          >
                            ×
                          </span>
                        </li>
                      ))
                    ) : (
                      <li style={{ padding: '10px', color: '#666', fontStyle: 'italic' }}>
                        Click here to add topics for this week
                      </li>
                    )}
                  </ul>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      );
    };
  
    return (
      <div className="lesson-plan-container">
        {/* Message area */}
        <div className="lesson_plan_message">
          {message && (
            <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-info'}`}>
              {message}
            </div>
          )}
        </div>
  
        {/* Header */}
        <h3>Lesson Plan</h3>
  
        {/* Instructions */}
        <div style={{ marginBottom: '10px' }}>
          <strong>How to use:</strong><br />
          • <strong>Click</strong> on the topic name to link scaffold to the topic and <b>create/edit evaluation questions</b>.<br />
          • <strong>Click ⚙️</strong> icon to change the <b>target level / difficulty</b> of the topic.<br />
          • <strong>Click ×</strong> to remove the topic from the week.<br />
          <br />
          <strong>Status indicators:</strong><br />
          • <span style={{color: '#337ab7', fontSize: '14px'}}>🎥</span> = Scaffolds are linked to this topic<br />
          • <span style={{color: 'green', fontSize: '14px'}}>✓</span> = Evaluation is complete (has questions)<br />
          • <span style={{color:'orange', fontSize: '14px'}}>✓</span> = Evaluation template is set up but not populated with questions yet
        </div>
  
        {/* Save button - only show for coordinators */}
        {isCoordinator && (
          <button 
            className="btn btn-success save-lesson-plan-btn"
            onClick={handleSaveLessonPlan}
            style={{ marginBottom: '20px', marginRight: '10px' }}
          >
            Save
          </button>
        )}

        {/* Question Stats button */}
        <button 
          className="btn btn-info"
          onClick={async () => {
            await loadTopicQuestionStats();
            setShowQuestionStats(true);
          }}
          style={{ marginBottom: '20px', marginRight: '10px' }}
          disabled={!lessonPlanWeeks || lessonPlanWeeks.length === 0}
        >
          View Question Statistics
        </button>
  
  
        {/* Lesson plan table */}
        {renderLessonPlanTable()}
  
        {/* Modals */}
        <TopicQuestionStatsModal 
          show={showQuestionStats}
          onHide={() => setShowQuestionStats(false)}
          nodesInLessonPlan={nodesInLessonPlan}
        />
  
        <SetTopicTargetLevelModal 
          show={showTargetLevel}
          onHide={() => setShowTargetLevel(false)}
          topicToSetTargetLevel={topicToSetTargetLevel}
          topicQuestionStats={topicQuestionStats}
          onSave={handleSaveTopicTargetLevel}
        />

        <TopicSearchModal 
          show={showTopicSearch}
          onHide={() => setShowTopicSearch(false)}
          week={weekForTopicSearch}
          lessonPlanWeeks={lessonPlanWeeks}
          onTopicSelected={handleTopicSelectedFromSearch}
        />

        <EvaluationQuestionEditorModal 
          show={showEvaluationEditor}
          onHide={handleEvaluationEditorClose}
          topicId={evaluationEditorData?.topicId}
          moduleSemesterId={evaluationEditorData?.moduleSemesterId}
          topicName={evaluationEditorData?.topicName}
        />

        <ScaffoldManagementModal 
          show={showScaffoldModal}
          onHide={handleScaffoldModalClose}
          scaffoldData={scaffoldData}
          onScaffoldDataUpdate={setScaffoldData}
          onOpenEvaluationEditor={(data) => {
            setEvaluationEditorData(data);
            setShowEvaluationEditor(true);
          }}
        />
      </div>
    );
  };
  
  export default LessonPlan;